import { EtlJob, EtlStatus } from '@api/apiClient';
import i18n from '@i18n/i18n';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';

// Define the types based on the API response structure
interface ValidationError {
  PropertyName: string;
  ErrorMessage: string;
  AttemptedValue: any;
  CustomState: any;
  Severity: number;
  ErrorCode: string | null;
  FormattedMessagePlaceholderValues: any;
}

interface TemplateRow {
  ModelExcelTemplate: Record<string, any>;
  Errors: ValidationError[];
}

interface TemplateErrors {
  Rows?: TemplateRow[];
  Errors?: {
    ModelExcelTemplate: any;
    Errors: ValidationError[];
  };
}

interface EtlJobReportProps {
  open: boolean;
  onClose: () => void;
  processingData: EtlJob[] | null;
}

const getSeverityColor = (severity: number): "error" | "warning" | "info" => {
  switch (severity) {
    case 0: return "error";
    case 1: return "warning";
    default: return "info";
  }
};

const getSeverityIcon = (severity: number) => {
  switch (severity) {
    case 0: return <ErrorIcon fontSize="small" />;
    case 1: return <WarningIcon fontSize="small" />;
    default: return <ErrorIcon fontSize="small" />;
  }
};

// Add helper function to parse error message
const parseErrorMessage = (errorMessage: string): string => {
  try {
    // Try to parse as JSON first
    const parsed = JSON.parse(errorMessage);
      
    // If it's a different JSON structure, return stringified version
    if (parsed && typeof parsed === 'object') {
      return JSON.stringify(parsed, null, 2);
    }
    
    // If it's a primitive value, return it as string
    return String(parsed);
  } catch {
    // If parsing fails, return the original string
    return errorMessage;
  }
};

// Calculate duration between two dates
const calculateDuration = (createdAt?: Date, completedAt?: Date): string => {
  if (!createdAt || !completedAt) {
    return 'N/A';
  }
  
  const start = new Date(createdAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;
  
  if (durationMs < 0) {
    return 'Invalid';
  }
  
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const EtlJobReport: React.FC<EtlJobReportProps> = ({ open, onClose, processingData }) => {
  if (!processingData || processingData.length === 0) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t('etl-job-report.title')}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>{i18n.t('etl-job-report.no-data')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{i18n.t('etl-job-report.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const job = processingData[0]; // Assuming single job for now
  
  // Parse templateErrors if it's a JSON string
  let parsedTemplateErrors: TemplateErrors | null = null;
  try {
    parsedTemplateErrors = typeof job.templateErrors === 'string' 
      ? JSON.parse(job.templateErrors) 
      : job.templateErrors;
  } catch (error) {
    console.error('Failed to parse templateErrors:', error);
  }
  
  const hasRowErrors = (parsedTemplateErrors?.Rows?.length || 0) > 0;
  const hasGeneralErrors = (parsedTemplateErrors?.Errors?.Errors?.length || 0) > 0;
  const hasErrors = hasRowErrors || hasGeneralErrors;
  
  const totalRowErrors = parsedTemplateErrors?.Rows?.reduce((sum: number, row: any) => sum + (row.Errors?.length || 0), 0) || 0;
  const totalGeneralErrors = parsedTemplateErrors?.Errors?.Errors?.length || 0;
  const totalErrors = totalRowErrors + totalGeneralErrors;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90%",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {i18n.t('etl-job-report.title')}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Job Summary */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.filename')}</Typography>
              <Typography variant="body1">{job.filename}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.type')}</Typography>
              <Typography variant="body1">{job.templateType}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.status')}</Typography>
              <Chip 
                label={i18n.t(`design-automation.status.${job.status}`)} 
                color={
                  job.status === EtlStatus.FailedInstructions ? 'error' : 
                  job.status === EtlStatus.Success ? 'success' : 
                  'default'
                } 
                size="small" 
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.created-at')}</Typography>
              <Typography variant="body1">
                {job.createdAt ? new Date(job.createdAt).toLocaleString() : i18n.t('etl-job-report.na')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.completed-at')}</Typography>
              <Typography variant="body1">
                {job.completedAt ? new Date(job.completedAt).toLocaleString() : i18n.t('etl-job-report.na')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.duration')}</Typography>
              <Typography variant="body1">{calculateDuration(job.createdAt, job.completedAt)}</Typography>
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.item-count')}</Typography>
              <Typography variant="body1">
                {(() => {
                  try {
                    const parsedData = typeof job.templateData === 'string' ? JSON.parse(job.templateData) : job.templateData;
                    return parsedData?.ItemCount || 0;
                  } catch {
                    return 0;
                  }
                })()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.material-count')}</Typography>
              <Typography variant="body1">
                {(() => {
                  try {
                    const parsedData = typeof job.templateData === 'string' ? JSON.parse(job.templateData) : job.templateData;
                    return parsedData?.MaterialCount || 0;
                  } catch {
                    return 0;
                  }
                })()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.rows-with-errors')}</Typography>
              <Typography variant="body1">{parsedTemplateErrors?.Rows?.length || 0}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{i18n.t('etl-job-report.total-errors')}</Typography>
              <Typography variant="body1" color="error.main">{totalErrors}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Errors Section */}
        {hasErrors ? (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon color="error" />
              {i18n.t('etl-job-report.processing-errors', { 
                totalErrors, 
                rowCount: (parsedTemplateErrors?.Rows?.length || 0) + (hasGeneralErrors ? 1 : 0)
              })}
            </Typography>
            
            {/* General/System Errors */}
            {hasGeneralErrors && (
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography>
                      {i18n.t('etl-job-report.system-errors')}
                    </Typography>
                    <Chip 
                      label={i18n.t(
                        totalGeneralErrors === 1 
                          ? 'etl-job-report.error-count' 
                          : 'etl-job-report.error-count-plural',
                        { count: totalGeneralErrors }
                      )}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  <Box>
                    <Typography gutterBottom>{i18n.t('etl-job-report.errors')}</Typography>
                    {parsedTemplateErrors?.Errors?.Errors?.map((error: ValidationError, errorIndex: number) => (
                      <Alert
                        key={errorIndex}
                        severity={getSeverityColor(error.Severity)}
                        icon={getSeverityIcon(error.Severity)}
                        sx={{ mb: 1 }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {i18n.t('etl-job-report.property-label', { propertyName: error.PropertyName })}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {parseErrorMessage(error.ErrorMessage)}
                          </Typography>
                          {error.ErrorCode && (
                            <Typography variant="caption" color="text.secondary">
                              {i18n.t('etl-job-report.error-code', { errorCode: error.ErrorCode })}
                            </Typography>
                          )}
                        </Box>
                      </Alert>
                    )) || []}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Row-specific Errors */}
            {parsedTemplateErrors?.Rows?.map((row: any, rowIndex: number) => (
              <Accordion key={rowIndex}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography>
                      {i18n.t('etl-job-report.row-label', {
                        rowNumber: rowIndex + 1,
                        partName: row.ModelExcelTemplate?.Part || i18n.t('etl-job-report.unknown-part')
                      })}
                    </Typography>
                    <Chip 
                      label={i18n.t(
                        row.Errors.length === 1 
                          ? 'etl-job-report.error-count' 
                          : 'etl-job-report.error-count-plural',
                        { count: row.Errors.length }
                      )}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails >
                  {/* Row Data */}
                  <Box sx={{ mb: 2 }}>
                    <Typography gutterBottom>{i18n.t('etl-job-report.row-data')}</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>{i18n.t('etl-job-report.property')}</strong></TableCell>
                            <TableCell><strong>{i18n.t('etl-job-report.value')}</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(row.ModelExcelTemplate || {}).map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell>{key}</TableCell>
                              <TableCell>{value?.toString() || i18n.t('etl-job-report.dash')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Errors */}
                  <Box>
                    <Typography gutterBottom>{i18n.t('etl-job-report.errors')}</Typography>
                    {row.Errors?.map((error: any, errorIndex: number) => (
                      <Alert
                        key={errorIndex}
                        severity={getSeverityColor(error.Severity)}
                        icon={getSeverityIcon(error.Severity)}
                        sx={{ mb: 1 }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {i18n.t('etl-job-report.property-label', { propertyName: error.PropertyName })}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {parseErrorMessage(error.ErrorMessage)}
                          </Typography>
                          {error.ErrorCode && (
                            <Typography variant="caption" color="text.secondary">
                              {i18n.t('etl-job-report.error-code', { errorCode: error.ErrorCode })}
                            </Typography>
                          )}
                        </Box>
                      </Alert>
                    )) || []}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )) || []}
          </Box>
        ) : (
          <Alert severity="success">
            <Typography>{i18n.t('etl-job-report.no-errors-found')}</Typography>
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{i18n.t('etl-job-report.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EtlJobReport;

